import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePipeline } from '@/api/queries/pipeline';
import { useEmpresaUpdate } from '@/api/mutations/empresa';
import { Skeleton } from '@/components/ui/skeleton';
import { BadgeSelect } from '@/components/BadgeSelect';
import { ScoreBadge } from '@/components/ScoreBadge';
import { cn } from '@/lib/cn';
import { displayName } from '@/lib/empresa';
import { toast } from 'sonner';
import type { Empresa, StatusFunil } from '@/api/types';

const ESTAGIOS: Record<
  string,
  { label: string; column: 'ativos' | 'pos' | 'fechados'; ordem: number; dot: string }
> = {
  abertura:         { label: 'abertura',         column: 'ativos',  ordem: 1,  dot: 'bg-blue-400' },
  qualificacao:     { label: 'qualificação',     column: 'ativos',  ordem: 2,  dot: 'bg-blue-500' },
  call_diagnostico: { label: 'call diagnóstico', column: 'ativos',  ordem: 3,  dot: 'bg-indigo-500' },
  diagnostico_pago: { label: 'diagnóstico pago', column: 'ativos',  ordem: 4,  dot: 'bg-violet-500' },
  proposta:         { label: 'proposta',         column: 'ativos',  ordem: 5,  dot: 'bg-purple-500' },
  objecao:          { label: 'objeção',          column: 'ativos',  ordem: 6,  dot: 'bg-amber-500' },
  fechamento:       { label: 'fechamento',       column: 'ativos',  ordem: 7,  dot: 'bg-emerald-500' },
  onboarding:       { label: 'onboarding',       column: 'pos',     ordem: 8,  dot: 'bg-emerald-400' },
  retencao:         { label: 'retenção',         column: 'pos',     ordem: 9,  dot: 'bg-green-500' },
  perdido:          { label: 'perdido',          column: 'fechados', ordem: 10, dot: 'bg-rose-500' },
  dead_onb:         { label: 'dead onb',         column: 'fechados', ordem: 11, dot: 'bg-rose-600' },
};

const TAB_LABELS = { ativos: 'Ativos', pos: 'Pós-venda', fechados: 'Fechados' } as const;

const STATUS_FUNIL_OPTIONS = [
  { value: 'lista', label: 'lista (voltar pra triage)' },
  ...Object.entries(ESTAGIOS).map(([value, e]) => ({ value, label: e.label })),
];

const tempDot: Record<string, string> = {
  frio: 'bg-[color:var(--color-temp-frio)]',
  morno: 'bg-[color:var(--color-temp-morno)]',
  quente: 'bg-[color:var(--color-temp-quente)]',
};

function EmpresaCard({ e }: { e: Empresa }) {
  const update = useEmpresaUpdate();
  async function moveTo(status: string) {
    try {
      await update.mutateAsync({ id: e.id, payload: { status_funil: status as StatusFunil } });
      toast.success(`→ ${ESTAGIOS[status]?.label ?? status}`);
    } catch (err) {
      toast.error('Falha: ' + (err instanceof Error ? err.message : 'erro'));
    }
  }
  return (
    <div className="group rounded-md border border-border/70 bg-card px-3 py-2.5 transition-colors hover:border-border">
      <Link to={`/empresas/${e.id}`} className="block">
        <div className="flex items-center gap-1.5">
          <span
            className={cn('inline-block h-1.5 w-1.5 rounded-full shrink-0', tempDot[e.temperatura_efetiva])}
            aria-label={e.temperatura_efetiva}
          />
          <span className="truncate text-[13px] font-medium text-foreground leading-tight">
            {displayName(e)}
          </span>
          {e.prioridade === 'alta' && (
            <span className="mono text-[10px] font-semibold text-destructive tracking-tight">!</span>
          )}
        </div>
      </Link>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="truncate text-[11px] text-muted-foreground/80 lowercase tracking-tight">
          {[e.setor, e.cidade].filter(Boolean).join(' · ')}
        </span>
        <ScoreBadge score={e.score_prospect} size="xs" />
      </div>
      <div className="mt-2 opacity-0 transition-opacity group-hover:opacity-100">
        <BadgeSelect
          value={e.status_funil}
          options={STATUS_FUNIL_OPTIONS}
          onSelect={moveTo}
        />
      </div>
    </div>
  );
}

function Column({ status, empresas }: { status: string; empresas: Empresa[] }) {
  const def = ESTAGIOS[status];
  if (!def) return null;
  return (
    <div className="flex w-64 shrink-0 flex-col">
      <div className="mb-2 flex items-baseline justify-between px-1">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {def.label}
        </h4>
        <span className="mono text-[10px] text-muted-foreground/70 tabular">
          {empresas.length}
        </span>
      </div>
      <div className="space-y-1.5 overflow-y-auto pr-1 pb-2" style={{ maxHeight: 'calc(100vh - 240px)' }}>
        {empresas.map((e) => (
          <EmpresaCard key={e.id} e={e} />
        ))}
        {empresas.length === 0 && (
          <p className="px-1 py-2 text-[11px] italic text-muted-foreground/60">vazio</p>
        )}
      </div>
    </div>
  );
}

export function PipelinePage() {
  const [tab, setTab] = useState<'ativos' | 'pos' | 'fechados'>('ativos');
  const { data, isLoading, error } = usePipeline();

  const groups = useMemo(() => {
    const g: Record<string, Empresa[]> = {};
    Object.keys(ESTAGIOS).forEach((k) => (g[k] = []));
    if (!data) return g;
    data.ativos.forEach((e) => {
      if (g[e.status_funil]) g[e.status_funil]!.push(e);
    });
    return g;
  }, [data]);

  const columnsByTab = useMemo(() => {
    const cols: Record<string, string[]> = { ativos: [], pos: [], fechados: [] };
    Object.entries(ESTAGIOS)
      .sort((a, b) => a[1].ordem - b[1].ordem)
      .forEach(([k, v]) => cols[v.column]!.push(k));
    return cols;
  }, []);

  const totals = useMemo(() => {
    const counts = data?.counts ?? {};
    const sumCols = (cols: string[]) => cols.reduce((s, k) => s + (counts[k] ?? 0), 0);
    return {
      ativos: sumCols(columnsByTab.ativos!),
      pos: sumCols(columnsByTab.pos!),
      fechados: sumCols(columnsByTab.fechados!),
    };
  }, [columnsByTab, data]);

  const totalAtivo = data?.ativos.length ?? 0;
  const listaTotal = data?.counts?.lista ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between border-b border-border/70 pb-3">
        <div>
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Pipeline</h1>
          <p className="mt-0.5 text-xs text-muted-foreground tabular">
            <span className="mono">{totalAtivo}</span> empresa{totalAtivo === 1 ? '' : 's'} ativas no funil
            {listaTotal > 0 && (
              <>
                {' · '}
                <Link to="/triage" className="hover:underline">
                  {listaTotal.toLocaleString('pt-BR')} em triage
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          {(['ativos', 'pos', 'fechados'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'inline-flex items-baseline gap-1.5 border-b-2 pb-2 transition-colors',
                tab === t
                  ? 'border-primary text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <span>{TAB_LABELS[t]}</span>
              <span className="mono text-[10px] text-muted-foreground/70 tabular">{totals[t]}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">Erro carregando pipeline.</p>}
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="overflow-x-auto pb-2 -mx-2 px-2">
          <div className="flex gap-3">
            {columnsByTab[tab]!.map((status) => (
              <Column key={status} status={status} empresas={groups[status] ?? []} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
